import { Controller } from "@hotwired/stimulus"

// Coordinates Ken Burns editing: preset, intensity, focal point
// Debounced auto-save to the API endpoint
export default class extends Controller {
  static targets = ["presetSelect", "intensitySlider", "intensityLabel",
                     "saveStatus", "presetGrid"]
  static values = {
    url: String,
    segmentIndex: Number,
    imageIndex: Number,
    saving: { type: Boolean, default: false }
  }

  connect() {
    this.saveTimeout = null
    this.currentData = this.collectCurrentData()
  }

  disconnect() {
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout)
      // Flush pending save
      this.save()
    }
  }

  // Called when preset dropdown changes
  presetChanged(event) {
    this.currentData.preset = event.target.value
    this.updatePresetGrid()
    this.updateSummaryDisplay()
    this.debouncedSave()
  }

  // Called when preset grid button is clicked
  selectPreset(event) {
    const preset = event.params.preset
    this.currentData.preset = preset

    // Update the select dropdown
    if (this.hasPresetSelectTarget) {
      this.presetSelectTarget.value = preset
    }

    this.updatePresetGrid()
    this.updateSummaryDisplay()
    this.debouncedSave()
  }

  // Called when intensity slider changes
  intensityChanged(event) {
    const value = parseFloat(event.target.value)
    this.currentData.intensity = value
    this.updateIntensityLabel(value)
    this.updateSummaryDisplay()
    this.debouncedSave()
  }

  // Called when focal point changes (via focal-point controller dispatch)
  focalPointChanged(event) {
    const { x, y } = event.detail
    this.currentData.focal_point = { x, y }
    this.debouncedSave()
  }

  // Reset all values to defaults
  resetDefaults() {
    this.currentData = {
      preset: "zoom-in-center",
      intensity: 0.5,
      focal_point: { x: 0.5, y: 0.5 }
    }

    // Update UI
    if (this.hasPresetSelectTarget) {
      this.presetSelectTarget.value = "zoom-in-center"
    }
    if (this.hasIntensitySliderTarget) {
      this.intensitySliderTarget.value = 0.5
      this.updateIntensityLabel(0.5)
    }

    // Reset focal point controller
    const focalPointController = this.element.querySelector("[data-controller='focal-point']")
    if (focalPointController) {
      const fpCtrl = this.application.getControllerForElementAndIdentifier(
        focalPointController, "focal-point"
      )
      if (fpCtrl) fpCtrl.reset()
    }

    this.updatePresetGrid()
    this.debouncedSave()
  }

  // Private methods

  collectCurrentData() {
    // Read focal point from the focal-point controller's values
    let focalPoint = { x: 0.5, y: 0.5 }
    const focalPointEl = this.element.querySelector("[data-controller='focal-point']")
    if (focalPointEl) {
      const x = parseFloat(focalPointEl.dataset.focalPointXValue)
      const y = parseFloat(focalPointEl.dataset.focalPointYValue)
      if (!isNaN(x) && !isNaN(y)) {
        focalPoint = { x, y }
      }
    }

    return {
      preset: this.hasPresetSelectTarget ? this.presetSelectTarget.value : "zoom-in-center",
      intensity: this.hasIntensitySliderTarget ? parseFloat(this.intensitySliderTarget.value) : 0.5,
      focal_point: focalPoint
    }
  }

  updateIntensityLabel(value) {
    if (!this.hasIntensityLabelTarget) return

    let label, colorClass
    if (value <= 0.35) {
      label = "Subtle"
      colorClass = "text-green-600"
    } else if (value <= 0.65) {
      label = "Medium"
      colorClass = "text-yellow-600"
    } else {
      label = "Dramatic"
      colorClass = "text-red-600"
    }

    const percent = Math.round(value * 100)
    this.intensityLabelTarget.textContent = `${label} (${percent}%)`
    this.intensityLabelTarget.className = `text-xs font-medium ${colorClass}`
  }

  updatePresetGrid() {
    if (!this.hasPresetGridTarget) return

    this.presetGridTarget.querySelectorAll("button").forEach(btn => {
      const isSelected = btn.dataset.kenBurnsEditorPresetParam === this.currentData.preset
      btn.classList.toggle("border-blue-500", isSelected)
      btn.classList.toggle("bg-blue-50", isSelected)
      btn.classList.toggle("text-blue-700", isSelected)
      btn.classList.toggle("border-gray-200", !isSelected)
      btn.classList.toggle("text-gray-600", !isSelected)
    })
  }

  debouncedSave() {
    this.updateSaveStatus("Unsaved changes...")
    this.dispatchPreviewUpdate()

    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout)
    }

    this.saveTimeout = setTimeout(() => {
      this.save()
    }, 800)
  }

  async save() {
    if (this.savingValue) return

    this.savingValue = true
    this.updateSaveStatus("Saving...")

    try {
      const csrfToken = document.querySelector("meta[name='csrf-token']")?.content

      const response = await fetch(this.urlValue, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
          "Accept": "application/json"
        },
        body: JSON.stringify({
          ken_burns: this.currentData
        })
      })

      if (response.ok) {
        this.updateSaveStatus("Saved")
        this.updateSummaryDisplay()
      } else {
        const data = await response.json()
        this.updateSaveStatus(`Error: ${data.error || "Failed to save"}`)
      }
    } catch (error) {
      console.error("Save failed:", error)
      this.updateSaveStatus("Save failed")
    } finally {
      this.savingValue = false
    }
  }

  updateSaveStatus(text) {
    if (this.hasSaveStatusTarget) {
      this.saveStatusTarget.textContent = text
    }
  }

  // Dispatch event so ken-burns-preview controllers can update their animation
  dispatchPreviewUpdate() {
    const detail = {
      preset: this.currentData.preset,
      intensity: this.currentData.intensity,
      focalX: this.currentData.focal_point?.x ?? 0.5,
      focalY: this.currentData.focal_point?.y ?? 0.5
    }

    // Find the associated thumbnail's preview controller via the image summary area
    const imageIndex = this.imageIndexValue
    const editorContainer = this.element.closest(`[data-image-editor="${imageIndex}"]`)
    if (!editorContainer) return

    const summarySection = editorContainer.previousElementSibling
    if (!summarySection) return

    const previewEl = summarySection.querySelector('[data-controller*="ken-burns-preview"]')
    if (previewEl) {
      const previewCtrl = this.application.getControllerForElementAndIdentifier(previewEl, "ken-burns-preview")
      if (previewCtrl) previewCtrl.update({ detail })
    }
  }

  updateSummaryDisplay() {
    // Update the preset label in the image summary above the editor
    const imageIndex = this.imageIndexValue
    const editorContainer = this.element.closest(`[data-image-editor="${imageIndex}"]`)
    if (!editorContainer) return

    // The summary section is the previous sibling of the editor container
    const summarySection = editorContainer.previousElementSibling
    if (!summarySection) return

    // Update the preset text in the info section
    const presetLabel = summarySection.querySelector(`[data-preset-label="${imageIndex}"]`)
    if (presetLabel) {
      presetLabel.textContent = this.currentData.preset
    }

    // Update the preset text in the thumbnail overlay
    const thumbnailPresetText = summarySection.querySelector('[data-kb-overlay="preset-text"]')
    if (thumbnailPresetText) {
      thumbnailPresetText.textContent = this.currentData.preset
    }

    // Update the intensity text in the thumbnail overlay
    const thumbnailIntensity = summarySection.querySelector('[data-kb-overlay="intensity"]')
    if (thumbnailIntensity && this.currentData.intensity !== undefined) {
      const value = this.currentData.intensity
      let label
      if (value <= 0.35) {
        label = "subtle"
      } else if (value <= 0.65) {
        label = "medium"
      } else {
        label = "dramatic"
      }
      thumbnailIntensity.textContent = label
    }
  }
}
